import { Apollo } from "apollo-angular";
import {
  Component,
  HostListener,
  ElementRef,
  ViewChild,
  OnInit,
} from "@angular/core";
import {
  Router,
  RouterEvent,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  ActivatedRoute,
  Scroll,
} from "@angular/router";
import { map, tap, filter, take } from "rxjs/operators";

import packageInfo from "../../package.json";
import { environment } from "src/environments/environment";
import { NgxRolesService } from "ngx-permissions";
import { UIPermissions } from "src/auth/permissions/permissions";
import { AuthNewService } from "src/auth/services/auth-new.service";
import { inOutAnimation } from "./utils/animations/in-out.animation";
import { GlobalLoadingService } from "./services/global-loading.service";
import { ApaiEnabledService } from "./modules/apai/services/apai-enabled.service";
import { CAN_MANAGE_DATA_MANAGEMENT } from "./app.constants";
import { VatEnabledService } from "./modules/apai/services/vat-enabled-service";
import { combineLatest } from "rxjs";
import { is } from "cypress/types/bluebird";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  animations: [inOutAnimation],
})
export class AppComponent implements OnInit {
  title = "w2v-management-app";
  version = null;

  expires$ = this.auth.expires$;
  isNavZindex = false;
  userEmail: string;
  userid: number;

  open = false;
  openMenu = false;
  loading = true;
  loading$ = this.globalLoadingService.isLoading$;

  auth$ = this.auth.auth$;
  fullname$ = this.auth.auth$.pipe(
    filter((data) => !!data),
    map((auth) => auth.payload),
    map(({ firstName, lastName }) => `${lastName}, ${firstName}`)
  );
  menuSwitcherOpen = false;
  menuMode: "vat" | "apai" = "vat";

  isApaiEnabled = false;
  isVatEnabled = true;

  get showMenuSwitcher() {
    return this.isApaiEnabled && this.isVatEnabled;
  }

  loggedIn = this.auth.auth$.pipe(
    tap(() => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const tokenValue = token.split(".")[1];
          const payload = JSON.parse(atob(tokenValue));

          if (payload["email"]) {
            this.userEmail = payload["email"];
          }
        }
      } catch (e) {
        //
      }
    })
  );

  @ViewChild("clickOutside") clickOutside: ElementRef;
  @ViewChild("menuSwitcher") menuSwitcher: ElementRef;

  @HostListener("document:click", ["$event.target"])
  public onClick(target) {
    const clickedInside = this.clickOutside?.nativeElement.contains(target);
    if (!clickedInside) {
      this.openMenu = false;
    }
    const clickedInsideMenuSwitcher =
      this.menuSwitcher?.nativeElement.contains(target);
    if (!clickedInsideMenuSwitcher) {
      this.menuSwitcherOpen = false;
    }
  }

  get canUseDataManagement() {
    return CAN_MANAGE_DATA_MANAGEMENT.includes(this.userEmail?.toLowerCase());
  }
  constructor(
    private auth: AuthNewService,
    private router: Router,
    private route: ActivatedRoute,
    private rolesService: NgxRolesService,
    private globalLoadingService: GlobalLoadingService,
    private apaiEnabledService: ApaiEnabledService,
    private vatEnabledService: VatEnabledService
  ) {
    // this.mixpanel.startTracking();

    this.router.events.subscribe((e: any) => {
      // console.log("navigate ", e);
      if (
        e instanceof NavigationEnd &&
        (e.url === "/change-password" || e.url === "/auth/change-password")
      ) {
        this.isNavZindex = true;
      } else if (!(e instanceof Scroll)) {
        this.isNavZindex = false;
      }
      this.navigationInterceptor(e);
    });

    this.route.queryParamMap
      .pipe(
        map((params) => params.has("version")),
        filter((version) => !!version),
        tap((t) => {
          this.version = packageInfo.version;
        })
      )
      .subscribe();

    if (!environment.production) {
      this.version = packageInfo.version;
    }
  }

  ngOnInit(): void {
    this.auth$.subscribe((auth) => {
      if (auth) {
        this.apaiEnabledService.fetchConfig();
        this.vatEnabledService.fetchConfig();
      }
    });

    combineLatest([
      this.apaiEnabledService.enabledCompanies$,
      this.vatEnabledService.enabledCompanies$,
    ]).subscribe(([apaiEnabled, vatEnabled]) => {
      const isApaiEnabled = apaiEnabled?.companies?.length > 0;
      const isVatEnabled = vatEnabled?.companies?.length > 0;

      if (isApaiEnabled) {
        this.isApaiEnabled = true;
      } else {
        this.isApaiEnabled = false;
      }

      if (apaiEnabled.userRole === "Admin") {
        this.isApaiEnabled = true;
      }
      if (apaiEnabled.userRole === "Analyst") {
        this.isApaiEnabled = false;
      }

      if (isVatEnabled) {
        this.isVatEnabled = true;
      } else {
        this.isVatEnabled = false;
      }

      if (vatEnabled.userRole === "Admin") {
        this.isVatEnabled = true;
      }
      /** if companies enabledProducts are empty vat is default product and must be enabled */
      if (!this.isApaiEnabled) {
        this.isVatEnabled = true;
      }

      const href = window.location.href;
      const isApaiHref = href?.includes("/apai");

      const userWantsToVisitApai = isApaiHref;
      const userWantsToVisitVat = !isApaiHref;
      if (userWantsToVisitApai && !this.isApaiEnabled) {
        const isAnalyist = vatEnabled.userRole === "Analyst";
        if (isAnalyist) {
          this.router.navigate(["/app/expenses/expenses"]);
        } else {
          this.router.navigate(["/app/dashboard"]);
        }

        this.menuMode = "vat";
      }
      if (userWantsToVisitVat && !this.isVatEnabled) {
        this.router.navigate(["/app/apai/dashboard"]);
        this.menuMode = "apai";
      }
      if (userWantsToVisitApai && this.isApaiEnabled) {
        this.menuMode = "apai";
      }
      if (userWantsToVisitVat && this.isVatEnabled) {
        this.menuMode = "vat";
      }
    });
  }

  getLsKey(userId: number) {
    return `${userId}-menu-mode`;
  }

  signOut() {
    this.auth.signOut();
  }

  navigationInterceptor(event: any): void {
    if (event instanceof NavigationStart) {
      const navState = this.router.getCurrentNavigation();
      /**preventLoader in state will prevent global loader
       * globalLoader always shown on first navigation when previousNavigation is null
       */
      if (
        !navState?.extras?.state?.["preventLoader"] ||
        !navState.previousNavigation
      )
        this.globalLoadingService.setGlobalLoading(true);
    }

    if (event instanceof NavigationEnd) {
      // this.loading = false;

      this.globalLoadingService.setGlobalLoading(false);
    }

    if (event instanceof NavigationCancel) {
      // this.loading = false;
      this.globalLoadingService.setGlobalLoading(false);
    }

    if (event instanceof NavigationError) {
      // this.loading = false;
      this.globalLoadingService.setGlobalLoading(false);
    }
  }

  onSwitchMenu(mode: "vat" | "apai") {
    this.menuMode = mode;
    this.menuSwitcherOpen = false;
    const menuKey = this.getLsKey(this.userid);
    localStorage.setItem(menuKey, mode);
    if (mode === "vat") {
      this.router.navigate(["/app/dashboard"]);
    }
    if (mode === "apai") {
      this.router.navigate(["/app/apai/dashboard"]);
    }
  }

  get production() {
    return environment.production;
  }

  get permissions() {
    return UIPermissions;
  }
}
